import classNames from "classnames";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { StrapiImage, StrapiProduct } from "~/shared-types";
import { getFormattedImageAttributes } from "~/utils/getImageAttributes";
import { Swiper, SwiperRef, SwiperSlide } from "swiper/react";
import { A11y, Pagination, Zoom } from "swiper";

import "swiper/swiper.min.css";
import "swiper/css/a11y";
import "swiper/css/pagination";
import { getAspectRatioClass } from "~/utils/getAspectRatioClass";
import { getOfferDetails } from "~/utils/getOfferDetails";
import { Badge } from "../Backroom/Badge";

export type ProductGalleryProps = {
  product: StrapiProduct;
};
export type ThumbnailProps = {
  image: StrapiImage;
  isActive: boolean;
  onClick: () => void;
};

function ProductGallery({ product }: ProductGalleryProps) {
  const { hasCurrentOffer, offerTitle } = getOfferDetails(product);
  const images = product.attributes.images.data;
  const { featuredImageOrientation } = product.attributes;

  if (!images || images.length === 0) return null;
  const swiperRef = useRef<SwiperRef>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  const goToSlide = (index: number) => {
    swiperRef.current?.swiper.slideTo(index);
  };

  useEffect(() => {
    const onSlideChange = () => {
      // istanbul ignore next
      setActiveSlide(swiperRef.current?.swiper.activeIndex ?? 0);
    };

    swiperRef.current?.swiper?.on("slideChange", onSlideChange);

    return () => {
      swiperRef.current?.swiper?.off("slideChange", onSlideChange);
    };
  }, []);

  return (
    <div className="mb-8 md:mb-20">
      <Swiper
        pagination
        zoom={true}
        autoHeight
        ref={swiperRef}
        modules={[A11y, Pagination, Zoom]}
      >
        {images.map((image, index) => (
          <SwiperSlide key={image.id}>
            <div className="relative 2xl:max-w-xl">
              <div className="swiper-zoom-container">
                <Image
                  {...getFormattedImageAttributes(image, "large")}
                  className={classNames(
                    getAspectRatioClass(
                      image,
                      index === 0 ? featuredImageOrientation : undefined
                    ),
                    "w-full md:mb-5 rounded-xl 2xl:max-w-xl object-cover max-h-[740px]"
                  )}
                />
                {hasCurrentOffer && (
                  <Badge color="yellow" className="absolute top-4 left-4">
                    {offerTitle}
                  </Badge>
                )}
              </div>
            </div>
          </SwiperSlide>
          ))}
      </Swiper>
      <div className="hidden md:grid grid-cols-4 xl:grid-cols-5 gap-3 lg:gap-5">
        {images.length <= 1
          ? null
          : images.map((image, index) => (
              <Thumbnail
                key={image.id}
                image={image}
                isActive={activeSlide === index}
                onClick={() => goToSlide(index)}
              />
            ))}
      </div>
    </div>
  );
}

function Thumbnail({ image, isActive, onClick }: ThumbnailProps) {
  return (
    <button
      className={classNames(isActive && "!ring-1 ring-primary-500 rounded-xl")}
      onClick={onClick}
    >
      <Image
        {...getFormattedImageAttributes(image, "small", {
          altFallback: `Thumbnail for ${image.attributes.name}`,
        })}
        // Thumbnails are ~125px in size
        className={classNames(
          "aspect-square object-cover rounded-xl transition contrast-[.85] hover:contrast-100",
          isActive && "!contrast-100"
        )}
      />
    </button>
  );
}

export { ProductGallery };
